import {
	BarChart3,
	Bell,
	Bot,
	Box,
	CheckCircle2,
	Clock,
	Code,
	Container,
	Database,
	FileCode,
	FileText,
	FolderTree,
	GitBranch,
	GitPullRequest,
	Globe,
	HardDrive,
	History,
	Key,
	KeyRound,
	Layers,
	ListTodo,
	Lock,
	MailWarning,
	Play,
	RefreshCw,
	Rocket,
	Search,
	Server,
	ShieldCheck,
	Sparkles,
	Table,
	Terminal,
	Timer,
	Upload,
	Users,
	Workflow,
	Zap,
} from "lucide-react";

import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

interface FeatureHighlight {
	icon: ReactNode;
	title: string;
	description: string;
}

interface CodeExample {
	title: string;
	description: string;
	filename: string;
	code: string;
}

export type FeatureCategory = "deploy" | "compute" | "data" | "ai" | "platform";

export interface FeatureInfo {
	slug: string;
	title: string;
	tagline: string;
	/** One-sentence card copy for the landing grid and /features index. */
	summary: string;
	description: string;
	demoTitle: string;
	icon: LucideIcon;
	/** Tailwind text color class used wherever the icon is rendered. */
	color: string;
	/** Hex of `color`, for the OpenGraph image. */
	hex: string;
	category: FeatureCategory;
	/** Path on the docs site, appended to the configured docs URL. */
	docsPath?: string;
	highlights: FeatureHighlight[];
	codeExample?: CodeExample;
}

export const featureCategories: { id: FeatureCategory; title: string }[] = [
	{ id: "deploy", title: "Deploy" },
	{ id: "compute", title: "Compute" },
	{ id: "data", title: "Data & storage" },
	{ id: "ai", title: "AI" },
	{ id: "platform", title: "Platform" },
];

export const featureData: Record<string, FeatureInfo> = {
	"auto-deploys": {
		slug: "auto-deploys",
		title: "Push to Deploy",
		tagline: "Every push ships, every branch gets a URL",
		summary:
			"Connect GitHub or push to Ploy Git. Production deploys from your main branch, every other branch gets its own preview URL.",
		description:
			"Connect a GitHub repository or push to a Ploy Git repository and every push builds and deploys automatically. The production branch goes live, every other branch gets a preview deployment with its own URL, and you can redeploy any previous build in one step.",
		demoTitle: "Live Deployment Timeline",
		icon: GitBranch,
		color: "text-emerald-500",
		hex: "#10b981",
		category: "deploy",
		docsPath: "/",
		highlights: [
			{
				icon: <GitPullRequest className="h-5 w-5 text-emerald-500" />,
				title: "Preview Deployments",
				description:
					"Every non-production branch gets its own deployment URL for review and testing.",
			},
			{
				icon: <RefreshCw className="h-5 w-5 text-emerald-500" />,
				title: "Instant Rollbacks",
				description:
					"Redeploy any previous deployment in one step if something goes wrong.",
			},
			{
				icon: <ShieldCheck className="h-5 w-5 text-emerald-500" />,
				title: "Skew Protection",
				description:
					"Clients stay pinned to the deployment that served their page, so a release mid-session never breaks a loaded app.",
			},
			{
				icon: <FolderTree className="h-5 w-5 text-emerald-500" />,
				title: "Monorepos",
				description:
					"Every ploy.yaml in a repository becomes its own project, sharing databases and queues by name.",
			},
			{
				icon: <Layers className="h-5 w-5 text-emerald-500" />,
				title: "Build Machine Sizes",
				description:
					"Pick a build machine size per organization when a large framework build needs more room.",
			},
			{
				icon: <Rocket className="h-5 w-5 text-emerald-500" />,
				title: "Deploy to Ploy Button",
				description:
					"Let users clone a GitHub template, fill in secrets, and deploy it to Ploy in one guided flow.",
			},
		],
		codeExample: {
			title: "Deployment Configuration",
			description:
				"Describe the build and the resources your app needs in a ploy.yaml at the root of the project.",
			filename: "ploy.yaml",
			code: `kind: dynamic
build: pnpm build
out: dist
db:
  DB: default
queue:
  TASKS: tasks
cron:
  DAILY_REPORT: "0 9 * * *"`,
		},
	},
	workers: {
		slug: "workers",
		title: "Workers",
		tagline: "Serverless runtime for apps and agents",
		summary:
			"Run TypeScript on an isolate-based serverless runtime that starts in milliseconds and scales to zero when idle.",
		description:
			"Run APIs, webhooks, full-stack frameworks, and agent code on an isolate-based serverless runtime that starts in milliseconds and scales to zero between requests. Write standard fetch handlers, or use Ploy Start for typed routes, and attach databases, queues, and storage with a line of ploy.yaml.",
		demoTitle: "Worker Code Example",
		icon: Rocket,
		color: "text-orange-500",
		hex: "#f97316",
		category: "compute",
		docsPath: "/workers",
		highlights: [
			{
				icon: <Zap className="h-5 w-5 text-orange-500" />,
				title: "Millisecond Cold Starts",
				description:
					"V8 isolates start in milliseconds, so there is no warm pool to manage and nothing running when traffic is zero.",
			},
			{
				icon: <Code className="h-5 w-5 text-orange-500" />,
				title: "Standard Web APIs",
				description:
					"fetch, Request, Response, and ctx.waitUntil() for background work after the response is sent.",
			},
			{
				icon: <FileCode className="h-5 w-5 text-orange-500" />,
				title: "Frameworks Included",
				description:
					"Deploy Next.js, Vite, and TanStack Start apps, or a bare worker, from the same ploy.yaml.",
			},
			{
				icon: <Server className="h-5 w-5 text-orange-500" />,
				title: "Bindings, Not Clients",
				description:
					"Databases, queues, state, files, and sandboxes arrive on env, typed by ploy types, with no connection strings.",
			},
		],
		codeExample: {
			title: "Worker Example",
			description:
				"A worker is a module with a fetch handler. Bindings declared in ploy.yaml show up on env.",
			filename: "src/index.ts",
			code: `export default {
  async fetch(request, env) {
    const url = new URL(request.url);

    if (url.pathname === "/api/users") {
      const { results } = await env.DB
        .prepare("SELECT id, name FROM users")
        .all();
      return Response.json({ users: results });
    }

    return new Response("Not Found", { status: 404 });
  },
} satisfies Ploy;`,
		},
	},
	"static-sites": {
		slug: "static-sites",
		title: "Static Sites & Frontends",
		tagline: "Ship the UI in front of your backend",
		summary:
			"Deploy static sites and single-page apps from any framework, served from a global edge cache on your own domain.",
		description:
			"Deploy static sites and single-page applications built with React, Vue, Svelte, Astro, or any static site generator. Assets are cached at the edge, invalidated on every deployment, and served on your own domain with automatic certificates.",
		demoTitle: "Asset Distribution",
		icon: FileCode,
		color: "text-blue-500",
		hex: "#3b82f6",
		category: "deploy",
		docsPath: "/vite",
		highlights: [
			{
				icon: <Globe className="h-5 w-5 text-blue-500" />,
				title: "Edge Cached",
				description:
					"Assets are cached close to your users and invalidated automatically on each deployment.",
			},
			{
				icon: <FileCode className="h-5 w-5 text-blue-500" />,
				title: "Any Framework",
				description:
					"Works with Vite, React, Vue, Svelte, Astro, and any tool that emits a static output directory.",
			},
			{
				icon: <Rocket className="h-5 w-5 text-blue-500" />,
				title: "Pair With a Worker",
				description:
					"Add a worker entry next to your Vite app and ship the API with the frontend in one project.",
			},
			{
				icon: <CheckCircle2 className="h-5 w-5 text-blue-500" />,
				title: "Custom Domains",
				description:
					"Connect your own domain with automatic certificates on the Pro plan.",
			},
		],
		codeExample: {
			title: "Static Configuration",
			description:
				"A static project only needs a build command and an output directory.",
			filename: "ploy.yaml",
			code: `kind: static
build: pnpm build
out: dist`,
		},
	},
	database: {
		slug: "database",
		title: "Databases",
		tagline: "Serverless SQLite for every project",
		summary:
			"Each project gets its own SQLite database with migrations from git, a SQL editor, table browser, and query insights.",
		description:
			"Every project can attach SQLite databases that persist across deployments. Migrations in your repository are applied automatically on deploy, and the dashboard gives you a SQL editor, table browser, and query performance insights.",
		demoTitle: "Database Explorer",
		icon: Database,
		color: "text-purple-500",
		hex: "#a855f7",
		category: "data",
		docsPath: "/databases",
		highlights: [
			{
				icon: <History className="h-5 w-5 text-purple-500" />,
				title: "Migrations From Git",
				description:
					"SQL migrations in your repository run in order on every deploy, including Drizzle-generated folders.",
			},
			{
				icon: <Play className="h-5 w-5 text-purple-500" />,
				title: "SQL Editor",
				description:
					"Run queries against production or a preview database directly in the dashboard.",
			},
			{
				icon: <Table className="h-5 w-5 text-purple-500" />,
				title: "Table Browser",
				description:
					"Browse tables, inspect schemas, and page through rows without writing a query.",
			},
			{
				icon: <BarChart3 className="h-5 w-5 text-purple-500" />,
				title: "Query Insights",
				description:
					"See your slowest and most frequent queries so you know what to index.",
			},
			{
				icon: <Code className="h-5 w-5 text-purple-500" />,
				title: "D1-Compatible API",
				description:
					"prepare, bind, batch, and exec, so existing D1 code and Drizzle setups port over unchanged.",
			},
			{
				icon: <Sparkles className="h-5 w-5 text-purple-500" />,
				title: "AI Query Generation",
				description:
					"Describe the data you need and let the dashboard draft the SQL for you.",
			},
		],
		codeExample: {
			title: "Database Binding",
			description:
				"Declare a database in ploy.yaml and query it through env with a D1-compatible API.",
			filename: "src/index.ts",
			code: `export default {
  async fetch(request, env) {
    const { results } = await env.DB
      .prepare("SELECT * FROM users")
      .all();

    return Response.json({ users: results });
  },
} satisfies Ploy;`,
		},
	},
	queues: {
		slug: "queues",
		title: "Queues",
		tagline: "Background jobs with retries built in",
		summary:
			"Send a message from any handler and process it in the background, with automatic retries and a dashboard to inspect every message.",
		description:
			"Process background jobs reliably. Send a message from a request handler, a cron run, or a workflow step, and a message handler in the same worker picks it up. Failed messages retry with exponential backoff, and every message is visible in the dashboard.",
		demoTitle: "Queue Dashboard",
		icon: ListTodo,
		color: "text-amber-500",
		hex: "#f59e0b",
		category: "compute",
		docsPath: "/queues",
		highlights: [
			{
				icon: <RefreshCw className="h-5 w-5 text-amber-500" />,
				title: "Automatic Retries",
				description:
					"Messages that throw are retried with exponential backoff, and the attempt number is passed to your handler.",
			},
			{
				icon: <Search className="h-5 w-5 text-amber-500" />,
				title: "Message Inspection",
				description:
					"View payloads, attempts, and status for every message in the dashboard.",
			},
			{
				icon: <MailWarning className="h-5 w-5 text-amber-500" />,
				title: "Failure Visibility",
				description:
					"Messages that exhaust their attempts are kept so you can see what went wrong.",
			},
			{
				icon: <Zap className="h-5 w-5 text-amber-500" />,
				title: "Same Worker, Same Env",
				description:
					"The message handler lives next to your fetch handler and uses the same database and storage bindings.",
			},
		],
		codeExample: {
			title: "Queue Consumer",
			description: "Send and process queue messages in your worker.",
			filename: "src/index.ts",
			code: `export default {
  async fetch(request, env) {
    await env.TASKS.send({
      task: "send-email",
      to: "user@example.com",
    });
    return Response.json({ queued: true });
  },

  async message(event) {
    console.log("Processing:", event.payload, event.attempt);
  },
} satisfies Ploy;`,
		},
	},
	workflows: {
		slug: "workflows",
		title: "Durable Workflows",
		tagline: "Multi-step jobs that survive failure",
		summary:
			"Break long-running work into steps that are persisted as they complete, so a failure only retries the step that failed.",
		description:
			"Build long-running, multi-step processes that survive failures. Each step is persisted as it completes; if a step throws, only that step is retried, three times with exponential backoff by default, and every run is inspectable step by step in the dashboard.",
		demoTitle: "Workflow Executions",
		icon: Workflow,
		color: "text-pink-500",
		hex: "#ec4899",
		category: "compute",
		docsPath: "/workflows",
		highlights: [
			{
				icon: <CheckCircle2 className="h-5 w-5 text-pink-500" />,
				title: "Durable Execution",
				description:
					"Completed steps are never repeated, even if the worker is restarted mid-run.",
			},
			{
				icon: <RefreshCw className="h-5 w-5 text-pink-500" />,
				title: "Per-Step Retries",
				description:
					"Three retries with exponential backoff by default, configurable per workflow or per step.",
			},
			{
				icon: <Clock className="h-5 w-5 text-pink-500" />,
				title: "Step Tracking",
				description:
					"Follow every execution step by step, with inputs, outputs, and timings.",
			},
			{
				icon: <Play className="h-5 w-5 text-pink-500" />,
				title: "Trigger From Anywhere",
				description:
					"Start a workflow from a request, a queue message, a cron run, or another workflow.",
			},
		],
		codeExample: {
			title: "Workflow Definition",
			description:
				"Define steps with step.run(). Each one is persisted when it finishes.",
			filename: "src/index.ts",
			code: `export default {
  async fetch(request, env) {
    const { executionId } = await env.ONBOARDING
      .trigger({ userId: "123" });
    return Response.json({ executionId });
  },

  workflows: {
    onboarding: {
      handler: async ({ input, step }) => {
        const user = await step.run("load-user", () =>
          loadUser(input.userId),
        );
        await step.run("send-welcome", () => sendEmail(user));
      },
    },
  },
} satisfies Ploy;`,
		},
	},
	cron: {
		slug: "cron",
		title: "Cron & Timers",
		tagline: "Run code on a schedule",
		summary:
			"Schedule handlers with cron expressions down to the second, or set durable one-shot and recurring timers from code.",
		description:
			"Run code on a schedule without a separate scheduler. Declare cron expressions in ploy.yaml, including six-field expressions for sub-minute schedules, and implement a scheduled handler. For delays and reminders set from code, durable timers fire your worker at a specific time and survive restarts.",
		demoTitle: "Scheduled Runs",
		icon: Clock,
		color: "text-teal-500",
		hex: "#14b8a6",
		category: "compute",
		docsPath: "/workers/cron",
		highlights: [
			{
				icon: <Clock className="h-5 w-5 text-teal-500" />,
				title: "Cron Expressions",
				description:
					"Standard five-field expressions, plus a leading seconds field for schedules like every ten seconds.",
			},
			{
				icon: <Timer className="h-5 w-5 text-teal-500" />,
				title: "Durable Timers",
				description:
					"Schedule a one-shot or recurring timer from code. Timers survive restarts and fire at least once.",
			},
			{
				icon: <Bell className="h-5 w-5 text-teal-500" />,
				title: "Multiple Triggers",
				description:
					"Name each schedule and branch on event.cron to run different jobs from one worker.",
			},
			{
				icon: <Terminal className="h-5 w-5 text-teal-500" />,
				title: "Runs Locally Too",
				description:
					"ploy dev fires your schedules on time and shows the run history in the local dashboard.",
			},
		],
		codeExample: {
			title: "Cron Triggers",
			description:
				"Each key in cron is a trigger name; the scheduled handler receives the expression that fired.",
			filename: "ploy.yaml",
			code: `kind: worker
build: pnpm build
out: dist
cron:
  EVERY_10_SECONDS: "*/10 * * * * *"
  HOURLY_CLEANUP: "0 * * * *"
  DAILY_REPORT: "0 9 * * *"`,
		},
	},
	storage: {
		slug: "storage",
		title: "State & Cache",
		tagline: "Key-value storage, durable or fast",
		summary:
			"Durable KV state with atomic JSON updates, and a Redis-backed cache with TTLs, both attached with one line of ploy.yaml.",
		description:
			"Two key-value stores for two jobs. State is durable, backed by SQLite, and exposes a KV-compatible API plus atomic JSON updates, so counters and documents never need a read-modify-write cycle. Cache is a fast, best-effort store with optional TTLs for rate limits, sessions, and computed results.",
		demoTitle: "State & Cache in Action",
		icon: HardDrive,
		color: "text-indigo-500",
		hex: "#6366f1",
		category: "data",
		docsPath: "/workers/state",
		highlights: [
			{
				icon: <KeyRound className="h-5 w-5 text-indigo-500" />,
				title: "KV-Compatible State",
				description:
					"get, put, delete, getWithMetadata, and list with the same shapes as the KV API you already know.",
			},
			{
				icon: <Sparkles className="h-5 w-5 text-indigo-500" />,
				title: "Atomic JSON Updates",
				description:
					"$set, $inc, $push, $pop, and $unset apply directly in storage, so concurrent writers never clobber each other.",
			},
			{
				icon: <Zap className="h-5 w-5 text-indigo-500" />,
				title: "Cache With TTL",
				description:
					"Set a value for sixty seconds or an hour and let it expire. Treat it as best-effort and fall back when it is gone.",
			},
			{
				icon: <Layers className="h-5 w-5 text-indigo-500" />,
				title: "Multiple Bindings",
				description:
					"Attach several state or cache stores to one worker and keep user data separate from config.",
			},
		],
		codeExample: {
			title: "State and Cache Bindings",
			description:
				"Durable state and an ephemeral cache, side by side in the same worker.",
			filename: "src/index.ts",
			code: `export default {
  async fetch(request, env) {
    await env.STATE.update("stats", { $inc: { visits: 1 } });

    const cached = await env.CACHE.get("report");
    if (cached) {
      return new Response(cached);
    }

    const report = await buildReport(env);
    await env.CACHE.set("report", report, { ttl: 600 });
    return new Response(report);
  },
} satisfies Ploy;`,
		},
	},
	"file-storage": {
		slug: "file-storage",
		title: "File Storage",
		tagline: "Object storage for uploads and documents",
		summary:
			"Store uploads, images, exports, and JSON documents with an S3-style put, get, list, and delete API, no bucket setup required.",
		description:
			"An object store attached to your project for user uploads, images, generated documents, and exports. Put and get files with a content type, list them by prefix, and serve them straight from a worker, with nothing to provision and no credentials to manage.",
		demoTitle: "File Browser",
		icon: FileText,
		color: "text-sky-500",
		hex: "#0ea5e9",
		category: "data",
		docsPath: "/workers/file-storage",
		highlights: [
			{
				icon: <Upload className="h-5 w-5 text-sky-500" />,
				title: "Put, Get, Delete, List",
				description:
					"An S3-style API with content types, prefix listing, and binary or text payloads.",
			},
			{
				icon: <FolderTree className="h-5 w-5 text-sky-500" />,
				title: "Organize by Prefix",
				description:
					"Keep uploads/user-123/ and documents/invoices/ apart and list either in one call.",
			},
			{
				icon: <Globe className="h-5 w-5 text-sky-500" />,
				title: "Serve From a Worker",
				description:
					"Return file.body with its content type and cache headers directly from your fetch handler.",
			},
			{
				icon: <Search className="h-5 w-5 text-sky-500" />,
				title: "Browse in the Dashboard",
				description:
					"Inspect what your app has stored without writing a script.",
			},
		],
		codeExample: {
			title: "Upload and Serve",
			description:
				"Store an upload with its content type, then serve it back with the right headers.",
			filename: "src/index.ts",
			code: `export default {
  async fetch(request, env) {
    const url = new URL(request.url);

    if (request.method === "POST") {
      const key = \`uploads/\${Date.now()}\`;
      await env.FILES.put(key, await request.arrayBuffer(), {
        contentType: request.headers.get("Content-Type") ?? "",
      });
      return Response.json({ key });
    }

    const file = await env.FILES.get(url.pathname.slice(1));
    if (!file) {
      return new Response("Not Found", { status: 404 });
    }
    return new Response(file.body, {
      headers: { "Content-Type": file.contentType },
    });
  },
} satisfies Ploy;`,
		},
	},
	sandboxes: {
		slug: "sandboxes",
		title: "Sandboxes",
		tagline: "Isolated environments your code controls",
		summary:
			"Run shell commands, clone repositories, and execute generated code in isolated, long-lived sandboxes from a worker.",
		description:
			"Give a worker an isolated, long-lived environment with its own filesystem. Run commands, write and read files, clone repositories, and start background processes that keep running after the response is sent. Each sandbox is addressed by id, so an agent session can keep one alive across requests.",
		demoTitle: "Sandbox Session",
		icon: Box,
		color: "text-lime-500",
		hex: "#84cc16",
		category: "ai",
		docsPath: "/sandboxes",
		highlights: [
			{
				icon: <Terminal className="h-5 w-5 text-lime-500" />,
				title: "Run Commands",
				description:
					"exec() returns stdout, stderr, and the exit code; execStream() streams output as it happens.",
			},
			{
				icon: <Play className="h-5 w-5 text-lime-500" />,
				title: "Background Processes",
				description:
					"Start a build, a dev server, or a coding agent and check back on it from a later request.",
			},
			{
				icon: <GitBranch className="h-5 w-5 text-lime-500" />,
				title: "Clone Repositories",
				description:
					"gitCheckout() clones public or private repositories without writing credentials to disk.",
			},
			{
				icon: <Bot className="h-5 w-5 text-lime-500" />,
				title: "Agent Ready",
				description:
					"With ai: true, the sandbox receives gateway credentials so a coding agent can call models with no keys of its own.",
			},
		],
		codeExample: {
			title: "Sandbox Binding",
			description:
				"Reuse the same id across requests to keep files and processes alive for a session.",
			filename: "src/index.ts",
			code: `export default {
  async fetch(request, env) {
    const sandbox = env.SANDBOX.get("session-42");

    await sandbox.gitCheckout("https://github.com/acme/app", {
      depth: 1,
      targetDir: "app",
    });

    const result = await sandbox.exec("pnpm test", { cwd: "app" });
    return Response.json({
      ok: result.success,
      output: result.stdout,
    });
  },
} satisfies Ploy;`,
		},
	},
	ai: {
		slug: "ai",
		title: "AI Gateway",
		tagline: "Models available to every worker",
		summary:
			"Add ai: true and call models through env.AI or any OpenAI-compatible SDK, with credentials managed per deployment.",
		description:
			"Call language models from any worker without managing provider keys. Add ai: true to ploy.yaml and your deployment receives an env.AI binding plus an OpenAI-compatible base URL and token, so existing SDKs work unchanged. Usage is metered per project, and the same credentials flow into sandboxes for coding agents.",
		demoTitle: "AI in a Worker",
		icon: Bot,
		color: "text-cyan-500",
		hex: "#06b6d4",
		category: "ai",
		docsPath: "/ai",
		highlights: [
			{
				icon: <Sparkles className="h-5 w-5 text-cyan-500" />,
				title: "env.AI.run()",
				description:
					"One call with a model name and inputs. No SDK required for the common case.",
			},
			{
				icon: <Code className="h-5 w-5 text-cyan-500" />,
				title: "OpenAI-Compatible",
				description:
					"Point the OpenAI or Anthropic SDK at the injected base URL and token and keep your existing code.",
			},
			{
				icon: <Key className="h-5 w-5 text-cyan-500" />,
				title: "No Provider Keys",
				description:
					"Tokens are minted per deployment and rotated for you, so agents never ship with a long-lived secret.",
			},
			{
				icon: <Bot className="h-5 w-5 text-cyan-500" />,
				title: "Agent SDK & Skills",
				description:
					"Build agents with @meetploy/agent-sdk, and let coding assistants scaffold Ploy apps with the published skills.",
			},
		],
		codeExample: {
			title: "AI Binding",
			description: "Enable AI in ploy.yaml and call a model from the worker.",
			filename: "src/index.ts",
			code: `export default {
  async fetch(request, env) {
    const { prompt } = await request.json();

    const result = await env.AI.run("glm-4.6v-flash", { prompt });

    return Response.json(result);
  },
} satisfies Ploy;`,
		},
	},
	auth: {
		slug: "auth",
		title: "Auth",
		tagline: "Managed user authentication",
		summary:
			"Email and password, passkeys, and Google, Apple, or GitHub sign-in, with sessions and React components, stored in your own database.",
		description:
			"Add user authentication with a single configuration option. Ploy Auth creates the user tables in a database you own, serves sign-in with email and password, passkeys, Google, Apple, and GitHub, each toggled per project from the dashboard, and ships React components for the forms, so your app can act on behalf of a signed-in user from the first deploy.",
		demoTitle: "Authentication Dashboard",
		icon: Lock,
		color: "text-rose-500",
		hex: "#f43f5e",
		category: "platform",
		docsPath: "/auth",
		highlights: [
			{
				icon: <ShieldCheck className="h-5 w-5 text-rose-500" />,
				title: "Secure by Default",
				description:
					"scrypt password hashing, a per-project signing secret, and rate-limited sign-in endpoints, with nothing for you to configure.",
			},
			{
				icon: <Key className="h-5 w-5 text-rose-500" />,
				title: "Session Cookies",
				description:
					"httpOnly cookie sessions with revocation, served from /_ploy/auth/* endpoints on your own domain.",
			},
			{
				icon: <Code className="h-5 w-5 text-rose-500" />,
				title: "React Components",
				description:
					"AuthProvider, SignInForm, and SignUpForm from @meetploy/auth-react, with dark mode support.",
			},
			{
				icon: <Users className="h-5 w-5 text-rose-500" />,
				title: "Your Database",
				description:
					"Users live in a SQLite database attached to your project, queryable like any other table.",
			},
		],
		codeExample: {
			title: "Auth Configuration",
			description: "Enable auth with a single line in your ploy.yaml file.",
			filename: "ploy.yaml",
			code: `kind: dynamic
build: pnpm build
out: dist
auth:
  binding: AUTH_DB`,
		},
	},
	"ploy-git": {
		slug: "ploy-git",
		title: "Ploy Git",
		tagline: "Deploy without GitHub",
		summary:
			"Push to a git repository hosted by Ploy from your laptop, CI, or an agent, and every push builds and deploys.",
		description:
			"Ploy Git is a git repository hosted by Ploy. Add it as a remote and push with any git client, from a laptop, a CI job, or an AI agent, and every push builds and deploys just like a GitHub-connected project. No GitHub account required, and you can move a project between GitHub and Ploy Git with its full history.",
		demoTitle: "Push to Ploy Git",
		icon: Terminal,
		color: "text-fuchsia-500",
		hex: "#d946ef",
		category: "deploy",
		docsPath: "/platform/ploy-git",
		highlights: [
			{
				icon: <GitBranch className="h-5 w-5 text-fuchsia-500" />,
				title: "Plain Git",
				description:
					"Any git client works. Pushes to the production branch go live; other branches get preview URLs.",
			},
			{
				icon: <KeyRound className="h-5 w-5 text-fuchsia-500" />,
				title: "Short-Lived Push URLs",
				description:
					"Credentials are minted on demand for one hour, a day, or thirty days, and never stored by Ploy.",
			},
			{
				icon: <Terminal className="h-5 w-5 text-fuchsia-500" />,
				title: "ploy push",
				description:
					"The CLI mints a fresh URL per push, so nothing lands in your git config.",
			},
			{
				icon: <RefreshCw className="h-5 w-5 text-fuchsia-500" />,
				title: "Move Either Way",
				description:
					"Switch an existing project from GitHub to Ploy Git, or back, with its history intact.",
			},
		],
		codeExample: {
			title: "Push From Anywhere",
			description:
				"Add the remote once, or let the CLI mint a URL for each push.",
			filename: "terminal",
			code: `# One-off push with a fresh, short-lived URL
ploy push --project proj_123

# Or keep a long-lived remote
ploy git remote --project proj_123 --ttl 30d --add
git push -u ploy main`,
		},
	},
	"self-hosting": {
		slug: "self-hosting",
		title: "Self-Hosting",
		tagline: "The whole platform, on your infrastructure",
		summary:
			"Run the entire platform with Docker on bare metal or any cloud. Open source under AGPLv3, free forever, no usage limits.",
		description:
			"Ploy is open source and the whole platform runs on your own infrastructure: the dashboard, the API, the builder, the runtime, databases, queues, and storage. Start with a single Docker container, or split services for production. The managed cloud runs the same code, so moving between them is not a rewrite.",
		demoTitle: "Self-Host in Minutes",
		icon: Server,
		color: "text-slate-500",
		hex: "#64748b",
		category: "platform",
		docsPath: "/self-host",
		highlights: [
			{
				icon: <Container className="h-5 w-5 text-slate-500" />,
				title: "One Container to Start",
				description:
					"A unified Docker image runs every service for testing and small deployments.",
			},
			{
				icon: <Layers className="h-5 w-5 text-slate-500" />,
				title: "Split for Production",
				description:
					"Run the API, builder, handler, and workers as separate services with Docker Compose.",
			},
			{
				icon: <Lock className="h-5 w-5 text-slate-500" />,
				title: "Your Data Stays Yours",
				description:
					"Code, environment variables, databases, and logs never leave infrastructure you control.",
			},
			{
				icon: <CheckCircle2 className="h-5 w-5 text-slate-500" />,
				title: "AGPLv3, Free Forever",
				description:
					"No licence fee, no usage limits, and the same features as the managed cloud.",
			},
		],
		codeExample: {
			title: "Quick Start",
			description:
				"Clone the repository, set your environment, and start the stack.",
			filename: "terminal",
			code: `git clone https://github.com/meetploy/ploy.git
cd ploy
cp .env.example .env
docker compose up -d

# Dashboard: http://localhost:3002`,
		},
	},
};

export const featureList: FeatureInfo[] = Object.values(featureData);
